import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { userSchema } from "../../utils/Schema";
import { userServices } from "../../services/AllServices";
import { useSelector } from "react-redux";
import { ADMIN_ROLES, LOGIN_MAX_FAILED_ATTEMPTS } from "../../utils/constants";

function AddEditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [data, setData] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const isJunior = user?.role === ADMIN_ROLES.JUNIOR;

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await userServices.getById({ id });
        setData(response?.data?.data?.result);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: userSchema,
    initialValues: {
      firstname: data?.firstname || "",
      lastname: data?.lastname || "",
      username: data?.username || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      failedLoginAttempts: data?.failedLoginAttempts ?? 0,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          id: id || undefined,
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          username: values.username.trim(),
          email: values.email,
          phoneNumber: values.phoneNumber,
        };

        if (id) {
          payload.failedLoginAttempts = Number(values.failedLoginAttempts) || 0;
        }

        const response = id
          ? await userServices.edit(payload)
          : await userServices.add(payload);
        toast.success(response?.data?.message, { toastId: "success" });

        const page = searchParams.get("page");
        const view = searchParams.get("view");

        if (page && view) {
          navigate(`/user-management?page=${page}&view=${view}`);
        } else if (page) {
          navigate(`/user-management?page=${page}`);
        } else if (view) {
          navigate(`/user-management?view=${view}`);
        } else {
          navigate(-1);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } =
    formik;

  const handleResetAttempts = async () => {
    if (!data || isJunior) return;

    setResetLoading(true);
    try {
      const response = await userServices.edit({
        id,
        firstname: data.firstname,
        lastname: data.lastname,
        username: data.username,
        email: data.email,
        phoneNumber: data.phoneNumber,
        resetLoginLockout: true,
      });
      toast.success(response?.data?.message, { toastId: "lockout-reset" });
      setFieldValue("failedLoginAttempts", 0);
      await fetchTaskData();
    } catch (error) {
      errorHandler(error);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} User</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} User`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    name="firstname"
                    value={values.firstname}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(touched.firstname && errors.firstname)}
                    helperText={touched.firstname && errors.firstname}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    required
                    name="lastname"
                    value={values.lastname}
                    onChange={handleChange}
                    error={Boolean(touched.lastname && errors.lastname)}
                    helperText={touched.lastname && errors.lastname}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="User Name"
                    required
                    name="username"
                    value={values.username}
                    onChange={handleChange}
                    error={Boolean(touched.username && errors.username)}
                    helperText={touched.username && errors.username}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    inputProps={{ maxLength: 50 }}
                    disabled={id && isJunior}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    required
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{ maxLength: 15 }}
                    disabled={id && isJunior}
                  />
                </Grid>

                {id && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Failed Login Attempts"
                      name="failedLoginAttempts"
                      value={values.failedLoginAttempts}
                      onChange={handleChange}
                      disabled={isJunior || resetLoading}
                      helperText={`Max lockout at ${LOGIN_MAX_FAILED_ATTEMPTS} attempts`}
                      inputProps={{ min: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Reset to 0">
                              <span>
                                <IconButton
                                  aria-label="reset failed login attempts"
                                  onClick={handleResetAttempts}
                                  disabled={isJunior || resetLoading}
                                  edge="end"
                                >
                                  <RestartAlt />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} User
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditUser;
